import { clampToBounds, closestPointOnSegment, distance, distanceSquared, midpoint } from './document.ts';
import { POLE_RADIUS_WORLD, pointHitsPole, poleCap, poleIntersectsRect } from './checkpointLook.ts';
import type { Rect } from './selection.ts';
import type { Checkpoint, CheckpointFlag, MapDocument, Vec2 } from './types.ts';

/** Next sequential order, ignoring unnumbered gates and the goal line. */
export function nextCheckpointOrder(map: MapDocument): number {
  let max = 0;

  for (const checkpoint of map.checkpoints) {
    if (checkpoint.isGoal || checkpoint.order === null || checkpoint.order <= 0) {
      continue;
    }

    if (checkpoint.order > max) {
      max = checkpoint.order;
    }
  }

  return max + 1;
}

/**
 * Remaps the positive orders still in use onto `1..k`. Gates that share a number still share
 * it. Unnumbered gates and the goal are left alone. A no-op when the sequence already has no holes.
 */
export function compactCheckpointOrders(map: MapDocument): void {
  const used: number[] = [];

  for (const checkpoint of map.checkpoints) {
    if (checkpoint.isGoal || checkpoint.order === null || checkpoint.order <= 0) {
      continue;
    }

    if (!used.includes(checkpoint.order)) {
      used.push(checkpoint.order);
    }
  }

  used.sort((left, right) => left - right);
  const remap = new Map<number, number>();

  used.forEach((order, index) => {
    remap.set(order, index + 1);
  });

  for (const checkpoint of map.checkpoints) {
    if (checkpoint.isGoal || checkpoint.order === null || checkpoint.order <= 0) {
      continue;
    }

    const next = remap.get(checkpoint.order);

    if (next !== undefined) {
      checkpoint.order = next;
    }
  }
}

export function addCheckpoint(map: MapDocument, a: Vec2, b: Vec2): Checkpoint {
  const checkpoint: Checkpoint = {
    id: `checkpoint-${map.nextCheckpointId}`,
    a: clampToBounds(a, map.bounds),
    b: clampToBounds(b, map.bounds),
    order: nextCheckpointOrder(map),
    isGoal: false,
  };

  map.nextCheckpointId += 1;
  map.checkpoints.push(checkpoint);
  return checkpoint;
}

export function findCheckpoint(map: MapDocument, checkpointId: string): Checkpoint | undefined {
  return map.checkpoints.find((checkpoint) => checkpoint.id === checkpointId);
}

export function gateWidth(checkpoint: Checkpoint): number {
  return distance(checkpoint.a, checkpoint.b);
}

/** Finds the closest flag shaft within `radius`; falls back to the closest gate line. */
export function findCheckpointAt(
  map: MapDocument,
  point: Vec2,
  radius: number,
  poleScale = 1,
): { id: string; flag: CheckpointFlag | null } | null {
  const pad = Math.max(radius, 0);
  const radiusSq = pad * pad;
  const scale = Number.isFinite(poleScale) && poleScale > 0 ? poleScale : 1;
  const shaftPad = Math.max(pad, POLE_RADIUS_WORLD * scale);
  let bestFlag: { id: string; flag: CheckpointFlag; distSq: number } | null = null;

  for (let i = map.checkpoints.length - 1; i >= 0; i -= 1) {
    const checkpoint = map.checkpoints[i];

    if (!checkpoint) {
      continue;
    }

    if (pointHitsPole(checkpoint.a, point, shaftPad, scale)) {
      const distSq = distanceSquared(
        point,
        closestPointOnSegment(point, checkpoint.a, poleCap(checkpoint.a, scale)),
      );
      if (!bestFlag || distSq < bestFlag.distSq) {
        bestFlag = { id: checkpoint.id, flag: 'a', distSq };
      }
    }

    if (pointHitsPole(checkpoint.b, point, shaftPad, scale)) {
      const distSq = distanceSquared(
        point,
        closestPointOnSegment(point, checkpoint.b, poleCap(checkpoint.b, scale)),
      );
      if (!bestFlag || distSq < bestFlag.distSq) {
        bestFlag = { id: checkpoint.id, flag: 'b', distSq };
      }
    }
  }

  if (bestFlag) {
    return { id: bestFlag.id, flag: bestFlag.flag };
  }

  let bestGate: { id: string; distSq: number } | null = null;

  for (let i = map.checkpoints.length - 1; i >= 0; i -= 1) {
    const checkpoint = map.checkpoints[i];

    if (!checkpoint) {
      continue;
    }

    const closest = closestPointOnSegment(point, checkpoint.a, checkpoint.b);
    const distSq = distanceSquared(point, closest);

    if (distSq <= radiusSq && (!bestGate || distSq < bestGate.distSq)) {
      bestGate = { id: checkpoint.id, distSq };
    }
  }

  return bestGate ? { id: bestGate.id, flag: null } : null;
}

/**
 * Which poles a marquee covers. Line-only (neither pole inside) counts as both, so boxing
 * the gate still selects the whole checkpoint.
 */
export function checkpointFlagsInRect(
  map: MapDocument,
  checkpointId: string,
  rect: Rect,
  poleScale = 1,
): CheckpointFlag[] {
  const checkpoint = findCheckpoint(map, checkpointId);

  if (!checkpoint) {
    return [];
  }

  const flags: CheckpointFlag[] = [];

  if (poleIntersectsRect(checkpoint.a, rect, poleScale)) {
    flags.push('a');
  }

  if (poleIntersectsRect(checkpoint.b, rect, poleScale)) {
    flags.push('b');
  }

  return flags.length > 0 ? flags : ['a', 'b'];
}

export function moveFlag(
  map: MapDocument,
  checkpointId: string,
  flag: CheckpointFlag,
  point: Vec2,
): boolean {
  const checkpoint = findCheckpoint(map, checkpointId);

  if (!checkpoint) {
    return false;
  }

  const clamped = clampToBounds(point, map.bounds);
  checkpoint[flag].x = clamped.x;
  checkpoint[flag].y = clamped.y;
  return true;
}

export function moveCheckpoint(map: MapDocument, checkpointId: string, delta: Vec2): boolean {
  const checkpoint = findCheckpoint(map, checkpointId);

  if (!checkpoint) {
    return false;
  }

  checkpoint.a = clampToBounds({ x: checkpoint.a.x + delta.x, y: checkpoint.a.y + delta.y }, map.bounds);
  checkpoint.b = clampToBounds({ x: checkpoint.b.x + delta.x, y: checkpoint.b.y + delta.y }, map.bounds);
  return true;
}

/** Resizes the gate symmetrically around its midpoint, preserving orientation. */
export function setGateWidth(map: MapDocument, checkpointId: string, width: number): boolean {
  const checkpoint = findCheckpoint(map, checkpointId);

  if (!checkpoint) {
    return false;
  }

  const clampedWidth = Math.max(1, width);
  const center = midpoint(checkpoint.a, checkpoint.b);
  const currentWidth = gateWidth(checkpoint);
  const dirX = currentWidth > 1e-6 ? (checkpoint.b.x - checkpoint.a.x) / currentWidth : 1;
  const dirY = currentWidth > 1e-6 ? (checkpoint.b.y - checkpoint.a.y) / currentWidth : 0;
  const half = clampedWidth / 2;

  checkpoint.a = clampToBounds({ x: center.x - dirX * half, y: center.y - dirY * half }, map.bounds);
  checkpoint.b = clampToBounds({ x: center.x + dirX * half, y: center.y + dirY * half }, map.bounds);
  return true;
}

export function setCheckpointOrder(map: MapDocument, checkpointId: string, order: number | null): boolean {
  const checkpoint = findCheckpoint(map, checkpointId);

  if (!checkpoint) {
    return false;
  }

  const next = order === null ? null : Math.max(1, Math.round(order));
  checkpoint.order = next;

  if (next === null) {
    compactCheckpointOrders(map);
  }

  return true;
}

/**
 * Setting a checkpoint as the goal unsets any other checkpoint's goal flag, clears this gate's
 * number, and closes the sequence. The previous goal, if it had no number, takes the next one.
 * Un-marking the goal puts it at the end of the sequence.
 */
export function setCheckpointGoal(map: MapDocument, checkpointId: string, isGoal: boolean): boolean {
  const checkpoint = findCheckpoint(map, checkpointId);

  if (!checkpoint) {
    return false;
  }

  if (!isGoal) {
    if (!checkpoint.isGoal) {
      return true;
    }

    checkpoint.isGoal = false;

    if (checkpoint.order === null) {
      checkpoint.order = nextCheckpointOrder(map);
    }

    return true;
  }

  const previous = map.checkpoints.filter((other) => other.isGoal && other.id !== checkpointId);
  const clearedNumber = checkpoint.order !== null;

  for (const other of map.checkpoints) {
    other.isGoal = other.id === checkpointId;
  }

  checkpoint.order = null;

  for (const old of previous) {
    if (old.order === null) {
      old.order = nextCheckpointOrder(map);
    }
  }

  if (clearedNumber || previous.length > 0) {
    compactCheckpointOrders(map);
  }

  return true;
}

export function removeCheckpoint(map: MapDocument, checkpointId: string): boolean {
  const index = map.checkpoints.findIndex((checkpoint) => checkpoint.id === checkpointId);

  if (index < 0) {
    return false;
  }

  map.checkpoints.splice(index, 1);
  compactCheckpointOrders(map);
  return true;
}
